import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  KeyValuePairs,
  Tabs,
  Grid,
  Alert,
  Flashbar,
  Pagination,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAuditSummary from 'src/hooks/useAuditSummary';
import ScoreBreakdown from 'src/components/audit/ScoreBreakdown';
import TasksTable from 'src/components/task/TasksTable';
import QuestionSummaryTable from 'src/components/audit/QuestionSummaryTable';
import {
  getAuditSummaryDefinition,
  flattenAuditDataForExport,
  getAuditSummaryExportConfig,
} from './auditSummaryConfig';
import { ExportButton } from 'src/components/ExportButton';
import QuestionsTable from 'src/components/audit/QuestionsTable';
import ReopenAuditModal from 'src/components/audit/ReopenAuditModal';
import CIProjectModal from 'src/components/audit/CIProjectModal';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useUserProfile from 'src/hooks/useUserProfile';
import { AuditStatus, CI_PORTAL_URL, ScoreFilterType } from 'src/contstant/commonLabels';
import { getCIModalStorageKey } from 'src/contstant/storageKeys';
import { emit } from 'src/v2/shared/observability/emit';
import { getFromLocalStorage, saveToLocalStorage } from 'src/utils/localStorageUtils';
import { formatDateTime } from 'src/utils/generalUtils';
import { useTasks } from 'src/store/tasks/useTasks';
import { useUsers } from 'src/store/users/useUsers';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import routes from 'src/contstant/routePaths';
import { round } from 'lodash';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { useBaseHook } from 'src/hooks/useBaseHook';
import { getIsPerformAudit, getIsReopenAudit } from 'src/helper/performAuditHelpers';
import { getPerformAuditUrl } from '../audits/auditsConfig';
import { useLocalPagination } from 'src/hooks/useLocalPagination';
import { TaskStatus } from 'src/contstant/taskStatusColors';
import { useSplitPanel } from 'src/context/SplitPanelContext';

// Constants for pagination
const QUESTIONS_PER_PAGE = 20; // Number of questions to display per page

/**
 * AuditSummaryPage - Displays comprehensive audit information including summary, questions, and tasks
 * Shows audit details, score breakdown, question responses, and associated tasks
 */
const AuditSummaryPage = () => {
  // Navigation and routing
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auditId = searchParams.get('auditId');
  const businessUnit = searchParams.get('businessUnit');

  // CI project prompt state
  const { enableCIProjectPrompt } = useFeatureFlags();
  const [ciModalVisible, setCiModalVisible] = useState(false); // Controls CI project modal visibility
  const hasShownCiModal = useRef(false); // Prevents modal re-showing after dismiss

  if (!auditId || !businessUnit) {
    navigate(routes.AUDITS.path, { replace: true });
    return <></>;
  }
  // Custom hooks for data management
  // Note: We don't use Redux auditSummary/currentAudit here - we use displayAuditSummary from polling hook
  const { checkUserPermissions, userPermission } = useUsers();
  const { businessUnitList } = useBusinessUnit();
  const { tasksList, loadTasks, fetchTasksApiStatus, createTaskFromQuestionApiStatus } =
    useTasks();
  const { closeView: closeSplitPanel } = useSplitPanel();
  const {
    getPersona,
    getUserProgramId,
    getUserBusinessUnitId,
    getDivisions,
    getUserId,
    currentUser,
    getPreferLanguageList,
  } = useUserProfile();

  // Component state
  const [activeTabId, setActiveTabId] = useState('summary'); // Controls which tab is active
  const [summaryFilter, setSummaryFilter] = useState<string | null>(
    ScoreFilterType.TOTAL,
  );
  const [questionResponseFilter, setQuestionResponseFilter] = useState<string | null>(
    ScoreFilterType.TOTAL,
  );
  const [reopenModalVisible, setReopenModalVisible] = useState(false); // Controls reopen modal visibility

  // Use custom polling hook instead of direct Redux state
  const {
    displayAuditSummary,
    currentAudit: polledCurrentAudit,
    statusesMatch,
    isPolling,
    isLoading: pollingIsLoading,
    errorMessage: pollingErrorMessage,
    refreshData,
  } = useAuditSummary({
    businessUnitId: businessUnit!,
    auditId: auditId!,
    additionalLanguages: getPreferLanguageList(),
  });

  // Use questions from displayAuditSummary (polling hook) which has complete data
  // Memoize based on the entire questions array to ensure updates when any question changes
  const allQuestions = useMemo(() => {
    return displayAuditSummary?.questions || [];
  }, [displayAuditSummary?.questions]);

  const hasLoadedAll = !!displayAuditSummary?.questions;
  const questionsLoading = pollingIsLoading;
  const questionsError = pollingErrorMessage;
  const totalQuestionsLoaded = allQuestions.length;

  /**
   * Filters questions based on selected filter criteria
   * Returns all questions if no filter or 'TOTAL' filter is selected
   */
  const filterQuestionsByResult = useCallback(
    (questions: typeof allQuestions, filter: string | null) => {
      if (!filter || filter === ScoreFilterType.TOTAL) return questions;

      return questions.filter((question) => {
        const result =
          question.responseDetails.mainQuestionResponse.responseResult?.toLowerCase();
        return result === (filter ? filter.toLowerCase() : null);
      });
    },
    [],
  );

  // Filter questions for Summary tab based on selected filter
  const summaryFilteredQuestions = filterQuestionsByResult(allQuestions, summaryFilter);

  // Local pagination for Summary tab (on filtered questions)
  const {
    currentPageIndex: summaryPageIndex,
    setCurrentPageIndex: setSummaryPageIndex,
    paginatedItems: summaryPaginatedQuestions,
    totalPages: summaryTotalPages,
  } = useLocalPagination({
    items: summaryFilteredQuestions,
    pageSize: QUESTIONS_PER_PAGE,
  });

  // Filter questions for Question Response tab based on selected filter
  const questionResponseFilteredQuestions = filterQuestionsByResult(
    allQuestions,
    questionResponseFilter,
  );

  // Local pagination for Question Response tab (on filtered questions)
  const {
    currentPageIndex: questionResponsePageIndex,
    setCurrentPageIndex: setQuestionResponsePageIndex,
    paginatedItems: questionResponsePaginatedQuestions,
    totalPages: questionResponseTotalPages,
  } = useLocalPagination({
    items: questionResponseFilteredQuestions,
    pageSize: QUESTIONS_PER_PAGE,
  });

  // Compute flattened data for export
  const exportData = useMemo(() => {
    if (!displayAuditSummary?.audit) return [];
    return flattenAuditDataForExport(displayAuditSummary.audit, allQuestions);
  }, [displayAuditSummary?.audit, allQuestions]);

  // Check if user has permission to edit any type of audit
  const isPerformAudit = useCallback(() => {
    return getIsPerformAudit(getPersona(), userPermission, displayAuditSummary?.audit);
  }, [getPersona, userPermission, displayAuditSummary?.audit]);

  const isReOpenAuditAllow = useCallback(() => {
    return getIsReopenAudit(getPersona(), userPermission, displayAuditSummary?.audit);
  }, [getPersona, userPermission, displayAuditSummary?.audit]);

  const isPerfectScoreAudit =
    enableCIProjectPrompt &&
    statusesMatch &&
    displayAuditSummary?.audit?.status === AuditStatus.COMPLETED &&
    displayAuditSummary?.audit?.metrics?.scorePercent === 100;

  const emitCIProjectClick = () => {
    emit('ci_project.create_clicked', {
      auditId,
      location: displayAuditSummary?.audit?.location,
      auditType: displayAuditSummary?.audit?.auditType,
      businessUnit,
      userId: getUserId(),
    });
  };

  // Load initial data on component mount - only once
  useEffect(() => {
    loadData();
  }, []); // Empty dependency array to ensure it only runs once

  // Close the split panel when navigating away from the audit summary page
  useEffect(() => {
    return () => {
      closeSplitPanel();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset CI modal state when navigating between audits (SPA navigation)
  useEffect(() => {
    setCiModalVisible(false);
    hasShownCiModal.current = false;
  }, [auditId]);

  // Show CI project modal on first load when audit scored 100% and not previously dismissed
  useEffect(() => {
    if (isPerfectScoreAudit && !hasShownCiModal.current) {
      const storageKey = getCIModalStorageKey(auditId, getUserId());
      const stored = getFromLocalStorage<{ completedAt: number } | null>(
        storageKey,
        null,
      );
      const auditCompletedAt = displayAuditSummary?.audit?.completionDate;
      if (!stored || stored.completedAt !== auditCompletedAt) {
        hasShownCiModal.current = true;
        setCiModalVisible(true);
      }
    }
  }, [isPerfectScoreAudit]);

  // Reload tasks when a new task is created from a question
  useEffect(() => {
    if (createTaskFromQuestionApiStatus.isSuccess) {
      loadTasks({
        businessUnitId: businessUnit,
        auditId: auditId,
        taskStatus: [
          TaskStatus.ASSIGNED,
          TaskStatus.IN_PROGRESS,
          TaskStatus.COMPLETED,
          TaskStatus.CANCELLED,
          TaskStatus.OVERDUE,
        ],
      });
    }
  }, [createTaskFromQuestionApiStatus.isSuccess]);

  // Check user permissions when current user changes
  useEffect(() => {
    checkUserPermissions({
      userId: getUserId(),
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      permissions: [
        permissionAccessConfig.CALIBRATION_AUDIT.PERFORM_CALIBRATION_AUDIT,
        permissionAccessConfig.PROGRESS_AUDIT.PERFORM_PROGRESS_AUDIT,
        permissionAccessConfig.SITUATION_AUDIT.PERFORM_SITUATION_AUDIT,
        permissionAccessConfig.MPA_AUDIT.PERFORM_MPA_AUDIT,
        permissionAccessConfig.CALIBRATION_AUDIT.REOPEN_CALIBRATION_AUDIT,
        permissionAccessConfig.PROGRESS_AUDIT.REOPEN_PROGRESS_AUDIT,
        permissionAccessConfig.SITUATION_AUDIT.REOPEN_SITUATION_AUDIT,
        permissionAccessConfig.MPA_AUDIT.REOPEN_MPA_AUDIT,
      ],
      divisions: getDivisions(),
    });
  }, [currentUser]);

  /**
   * Loads audit summary and associated tasks data
   */
  function loadData() {
    // Use refreshData from the polling hook
    refreshData();
    loadTasks({
      businessUnitId: businessUnit!,
      auditId: auditId!,
      taskStatus: [
        TaskStatus.ASSIGNED,
        TaskStatus.IN_PROGRESS,
        TaskStatus.COMPLETED,
        TaskStatus.CANCELLED,
        TaskStatus.OVERDUE,
      ],
    });
  }

  /**
   * Renders the audit header with title, metadata, and overall score
   */
  function AuditHeader() {
    return (
      <Grid
        data-testid="audit-header-grid"
        gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}
      >
        <Header
          data-testid="audit-header"
          variant="h1"
          description={
            <>
              <span data-testid="audit-id">
                Audit ID: {displayAuditSummary?.audit.auditId}
              </span>
              {displayAuditSummary?.audit?.completionDate ? (
                <>
                  <span> • </span>
                  <span data-testid="completion-date">
                    Completed on{' '}
                    {formatDateTime(displayAuditSummary?.audit?.completionDate)}
                  </span>
                </>
              ) : (
                <>
                  <span> • </span>
                  <span data-testid="scheduled-date">
                    Scheduled on :{' '}
                    {formatDateTime(displayAuditSummary?.audit?.scheduledDate)}
                  </span>
                </>
              )}

              {displayAuditSummary?.audit?.createdBy && (
                <>
                  <span> • </span>
                  <span data-testid="created-by">
                    By {displayAuditSummary?.audit?.createdBy}
                  </span>
                </>
              )}
            </>
          }
        >
          {displayAuditSummary?.audit.name}
        </Header>
        {displayAuditSummary?.audit?.metrics?.scorePercent !== undefined &&
          displayAuditSummary?.audit?.status === AuditStatus.COMPLETED &&
          statusesMatch && (
            <Box
              data-testid="overall-score-container"
              float="right"
              padding={{ horizontal: 'xs' }}
            >
              <SpaceBetween
                data-testid="score-spacer"
                direction="vertical"
                size="xs"
                alignItems="center"
              >
                <Box data-testid="score-box" padding="xs">
                  <SpaceBetween
                    data-testid="score-content"
                    direction="vertical"
                    size="xxxs"
                  >
                    <Box
                      data-testid="score-percentage"
                      textAlign="center"
                      fontSize="display-l"
                      color="text-status-warning"
                    >
                      {round(displayAuditSummary?.audit?.metrics.scorePercent || 0, 2)}%
                    </Box>
                    <Box data-testid="score-label" color="text-status-warning">
                      Score Percent
                    </Box>
                  </SpaceBetween>
                </Box>
              </SpaceBetween>
            </Box>
          )}
      </Grid>
    );
  }

  // Loading state
  if (pollingIsLoading) {
    return (
      <Flashbar
        data-testid="loading-flashbar"
        items={[
          {
            type: 'info',
            loading: true,
            header: 'Loading in Progress',
            content: 'Loading details. This may take a few moments.',
            id: 'loading',
          },
        ]}
      />
    );
  }

  // Error state
  if (pollingErrorMessage) {
    return (
      <Alert data-testid="error-alert" type="error">
        Error loading summary details: {pollingErrorMessage}
      </Alert>
    );
  }

  function openPerformAuditPage(auditId: string, businessUnit: string) {
    const params = new URLSearchParams({
      auditId: auditId,
      businessUnit: businessUnit,
    });
    navigate(`${routes.PERFORM_AUDIT.path}?${params}`);
  }

  return (
    <Box data-testid="audit-summary-page" margin={{ top: 's' }}>
      <SpaceBetween data-testid="audit-summary-content" size="m">
        <Container data-testid="audit-header-container" header={<AuditHeader />}>
          <SpaceBetween data-testid="audit-actions" size="m" direction="horizontal">
            {/* Export button - exports audit summary with all questions and sub-questions */}

            {/* Reopen audit button - only enabled for completed/expired audits and when states match */}
            {isReOpenAuditAllow() && (
              <Button
                data-testid="reopen-audit-button"
                variant="primary"
                iconName="refresh"
                disabled={
                  !statusesMatch ||
                  (displayAuditSummary?.audit?.status !== AuditStatus.COMPLETED &&
                    displayAuditSummary?.audit?.status !== AuditStatus.EXPIRED)
                }
                onClick={() => setReopenModalVisible(true)}
              >
                Reopen Audit
              </Button>
            )}
            {/* Perform audit button - only shown for users with edit permissions on non-completed audits */}
            {isPerformAudit() &&
              statusesMatch &&
              (displayAuditSummary?.audit?.status === AuditStatus.NOT_STARTED ||
                displayAuditSummary?.audit?.status === AuditStatus.IN_PROGRESS) && (
                <Button
                  data-testid="perform-audit-button"
                  variant="primary"
                  iconName="file-open"
                  href={getPerformAuditUrl(displayAuditSummary.audit)}
                  onClick={(event) => {
                    event.preventDefault();
                    openPerformAuditPage(auditId, businessUnit);
                  }}
                >
                  Perform Audit
                </Button>
              )}

            {statusesMatch &&
              displayAuditSummary?.audit?.status === AuditStatus.COMPLETED && (
                <Button
                  data-testid="view-audit-button"
                  variant="primary"
                  iconName="file-open"
                  href={getPerformAuditUrl(displayAuditSummary.audit)}
                  onClick={(event) => {
                    event.preventDefault();
                    openPerformAuditPage(auditId, businessUnit);
                  }}
                >
                  View Audit
                </Button>
              )}
            <ExportButton
              buttonVariant="normal"
              data-testid="export-audit-summary-button"
              data={exportData}
              config={getAuditSummaryExportConfig(
                displayAuditSummary?.audit?.auditId,
                displayAuditSummary?.audit?.businessUnit,
              )}
              buttonText="Export"
              disabled={!statusesMatch || pollingIsLoading}
            />
            {isPerfectScoreAudit && (
              <Button
                data-testid="ci-project-inline-button"
                variant="primary"
                iconName="suggestions"
                iconAlign="left"
                href={CI_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={emitCIProjectClick}
              >
                Explore CI Project
              </Button>
            )}
          </SpaceBetween>
          {isPerfectScoreAudit && (
            <Box data-testid="ci-project-banner" margin={{ top: 's' }}>
              <SpaceBetween direction="horizontal" size="xs">
                <span aria-hidden="true">↗</span>
                <span>
                  Perfect score! Start a Continuous Improvement project to capture the
                  next opportunity for {displayAuditSummary?.audit?.location}.
                </span>
              </SpaceBetween>
            </Box>
          )}
          <ReopenAuditModal
            data-testid="reopen-audit-modal"
            visible={reopenModalVisible}
            onDismiss={() => setReopenModalVisible(false)}
            auditId={auditId}
            businessUnitId={businessUnit}
            onSuccess={() => {
              setReopenModalVisible(false);
              openPerformAuditPage(auditId, businessUnit);
            }}
          />
          <CIProjectModal
            visible={ciModalVisible && isPerfectScoreAudit}
            onDismiss={() => {
              setCiModalVisible(false);
              saveToLocalStorage(getCIModalStorageKey(auditId, getUserId()), {
                completedAt: displayAuditSummary?.audit?.completionDate,
              });
            }}
            onCreateClick={emitCIProjectClick}
            locationName={displayAuditSummary?.audit?.location || ''}
          />
        </Container>

        {/* Main content tabs */}
        <Tabs
          data-testid="audit-summary-tabs"
          activeTabId={activeTabId}
          onChange={({ detail }) => {
            setActiveTabId(detail.activeTabId);
            if (detail.activeTabId !== 'question-response') {
              closeSplitPanel();
            }
          }}
          tabs={[
            {
              id: 'summary',
              label: 'Summary',
              content: (
                <SpaceBetween data-testid="summary-tab-content" size="m">
                  {/* Audit Details section */}
                  {displayAuditSummary?.audit && (
                    <Container
                      data-testid="audit-details-container"
                      header={
                        <Header data-testid="audit-details-header" variant="h3">
                          Audit Details
                        </Header>
                      }
                    >
                      <KeyValuePairs
                        data-testid="audit-details-keyvalue"
                        columns={3}
                        items={getAuditSummaryDefinition(
                          displayAuditSummary,
                          businessUnitList,
                          polledCurrentAudit,
                        )}
                      />
                    </Container>
                  )}
                  {/* Synchronization message or Score breakdown + Questions */}
                  {!statusesMatch && polledCurrentAudit && displayAuditSummary?.audit ? (
                    <Container data-testid="syncing-container">
                      <Flashbar
                        data-testid="syncing-flashbar"
                        items={[
                          {
                            type: 'info',
                            loading: true,
                            header: 'Synchronizing Audit Data',
                            content: 'This may take a few moments.',
                            id: 'syncing',
                          },
                        ]}
                      />
                    </Container>
                  ) : statusesMatch ? (
                    <>
                      {/* Score breakdown with filtering - only show when synchronized */}
                      <ScoreBreakdown
                        data-testid="score-breakdown"
                        auditSummary={displayAuditSummary}
                        selectedFilter={summaryFilter}
                        onFilterChange={(filter) => {
                          setSummaryFilter(filter);
                          setSummaryPageIndex(1); // Reset to first page when filter changes
                        }}
                        allQuestions={allQuestions}
                      />
                      {/* Questions loading state */}
                      {questionsLoading && (
                        <Alert data-testid="questions-loading-alert" type="info">
                          Loading all questions... ({totalQuestionsLoaded} loaded)
                        </Alert>
                      )}
                      {/* Questions error state */}
                      {questionsError && (
                        <Alert data-testid="questions-error-alert" type="error">
                          Error loading questions: {questionsError}
                        </Alert>
                      )}
                      {/* Questions table with pagination - only show when synchronized */}
                      {hasLoadedAll && allQuestions.length > 0 && (
                        <QuestionsTable
                          data-testid="questions-table"
                          audit={displayAuditSummary!.audit}
                          questions={summaryPaginatedQuestions}
                          selectedFilter={summaryFilter}
                          isLoading={questionsLoading}
                          showLanguageDropdown={true}
                          paginationControls={
                            <Pagination
                              data-testid="pagination-summary"
                              currentPageIndex={summaryPageIndex}
                              pagesCount={summaryTotalPages}
                              onChange={({ detail }) =>
                                setSummaryPageIndex(detail.currentPageIndex)
                              }
                            />
                          }
                        />
                      )}
                      {/* Empty state when no questions */}
                      {hasLoadedAll && allQuestions.length === 0 ? (
                        displayAuditSummary?.audit.status === AuditStatus.SCHEDULED ? (
                          <Alert data-testid="scheduled-audit-alert" type="info">
                            Audit questions will be available after the audit moves into
                            "Not Started" status.
                          </Alert>
                        ) : (
                          <Alert data-testid="no-questions-alert" type="info">
                            No questions found for this audit.
                          </Alert>
                        )
                      ) : null}
                    </>
                  ) : null}
                </SpaceBetween>
              ),
            },
            {
              id: 'question-response',
              label: 'Question Response',
              content:
                !statusesMatch && polledCurrentAudit && displayAuditSummary?.audit ? (
                  <Container data-testid="syncing-container-question-response">
                    <Flashbar
                      data-testid="syncing-flashbar-question-response"
                      items={[
                        {
                          type: 'info',
                          loading: true,
                          header: 'Synchronizing Audit Data',
                          content: 'This may take a few moments.',
                          id: 'syncing-question-response',
                        },
                      ]}
                    />
                  </Container>
                ) : statusesMatch ? (
                  <SpaceBetween data-testid="question-response-tab-content" size="m">
                    <ScoreBreakdown
                      data-testid="question-response-score-breakdown"
                      auditSummary={displayAuditSummary}
                      selectedFilter={questionResponseFilter}
                      onFilterChange={(filter) => {
                        setQuestionResponseFilter(filter);
                        setQuestionResponsePageIndex(1); // Reset to first page when filter changes
                      }}
                      allQuestions={allQuestions}
                    />
                    {/* Questions loading state */}
                    {questionsLoading && (
                      <Alert data-testid="questions-loading-alert-qr" type="info">
                        Loading all questions... ({totalQuestionsLoaded} loaded)
                      </Alert>
                    )}
                    {/* Questions error state */}
                    {questionsError && (
                      <Alert data-testid="questions-error-alert-qr" type="error">
                        Error loading questions: {questionsError}
                      </Alert>
                    )}
                    {/* Questions table with pagination */}
                    {hasLoadedAll && allQuestions.length > 0 && (
                      <QuestionSummaryTable
                        data-testid="question-summary-table"
                        questions={questionResponsePaginatedQuestions}
                        isLoading={questionsLoading}
                        audit={displayAuditSummary!.audit}
                        tasks={tasksList}
                        selectedFilter={questionResponseFilter}
                        showLanguageDropdown={true}
                        paginationControls={
                          <Pagination
                            data-testid="pagination-question-response"
                            currentPageIndex={questionResponsePageIndex}
                            pagesCount={questionResponseTotalPages}
                            onChange={({ detail }) =>
                              setQuestionResponsePageIndex(detail.currentPageIndex)
                            }
                          />
                        }
                        onTaskCreated={() => {
                          loadTasks({
                            businessUnitId: businessUnit!,
                            auditId: auditId!,
                            taskStatus: [
                              TaskStatus.ASSIGNED,
                              TaskStatus.IN_PROGRESS,
                              TaskStatus.COMPLETED,
                              TaskStatus.CANCELLED,
                              TaskStatus.OVERDUE,
                            ],
                          });
                        }}
                      />
                    )}
                    {/* Empty state when no questions */}
                    {hasLoadedAll && allQuestions.length === 0 ? (
                      displayAuditSummary?.audit.status === AuditStatus.SCHEDULED ? (
                        <Alert data-testid="scheduled-audit-alert" type="info">
                          Audit questions will be available after the audit moves into
                          "Not Started" status.
                        </Alert>
                      ) : (
                        <Alert data-testid="no-questions-alert" type="info">
                          No questions found for this audit.
                        </Alert>
                      )
                    ) : null}
                  </SpaceBetween>
                ) : null,
            },
            {
              id: 'task',
              label: 'Task',
              content: (
                <TasksTable
                  data-testid="tasks-table"
                  tasks={tasksList}
                  fetchTasksApiStatus={fetchTasksApiStatus}
                />
              ),
            },
          ]}
        />
      </SpaceBetween>
    </Box>
  );
};

export default AuditSummaryPage;
